import type {
  DayOfWeek,
  OrganizationNotificationSettingsDto,
  OrganizationSettingsDto,
} from "@fp/admin-api-client";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { Toggle } from "@fp/ui/components/base/toggle/toggle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDebouncedCallback } from "use-debounce";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import axios, { type ApiErrorResponse } from "@/lib/axios";

export const Route = createFileRoute("/settings/notifications/")({
  component: RouteComponent,
});

const daysOfWeek = [
  { value: "Sunday", label: "Sunday" },
  { value: "Monday", label: "Monday" },
  { value: "Tuesday", label: "Tuesday" },
  { value: "Wednesday", label: "Wednesday" },
  { value: "Thursday", label: "Thursday" },
  { value: "Friday", label: "Friday" },
  { value: "Saturday", label: "Saturday" },
];

function RouteComponent() {
  const queryClient = useQueryClient();

  const { data: orgSettings } = useQuery({
    queryKey: ["organizationSettings"],
    queryFn: async () => {
      const response = await axios.get<OrganizationSettingsDto>(
        "/api/organizations/current/settings",
      );
      return response.data;
    },
  });

  const [isLowBalanceEnabled, setIsLowBalanceEnabled] = useState(
    orgSettings?.notifications.lowBalanceNotification.enabled ?? true,
  );
  const [lowBalanceThreshold, setLowBalanceThreshold] = useState(
    (
      orgSettings?.notifications.lowBalanceNotification.threshold ?? 2000
    ).toFixed(0),
  );
  const [isDigestEnabled, setIsDigestEnabled] = useState(
    orgSettings?.notifications.pendingPayoutDigest.enabled ?? true,
  );
  const [digestSendTime, setDigestSendTime] = useState(
    orgSettings?.notifications.pendingPayoutDigest.sendTime ?? "17:00",
  );
  const [isWeeklyDigestEnabled, setIsWeeklyDigestEnabled] = useState(
    orgSettings?.notifications.weeklyEarningsDigest?.enabled ?? false,
  );
  const [weeklyDigestDayOfWeek, setWeeklyDigestDayOfWeek] = useState<DayOfWeek>(
    orgSettings?.notifications.weeklyEarningsDigest?.dayOfWeek ?? "Monday",
  );
  const [weeklyDigestSendTime, setWeeklyDigestSendTime] = useState(
    orgSettings?.notifications.weeklyEarningsDigest?.sendTime ?? "08:00",
  );

  useEffect(() => {
    if (!orgSettings) return;
    setIsLowBalanceEnabled(
      orgSettings.notifications.lowBalanceNotification.enabled,
    );
    setLowBalanceThreshold(
      orgSettings.notifications.lowBalanceNotification.threshold.toFixed(0),
    );
    setIsDigestEnabled(orgSettings.notifications.pendingPayoutDigest.enabled);
    setDigestSendTime(
      orgSettings.notifications.pendingPayoutDigest.sendTime ?? "17:00",
    );
    setIsWeeklyDigestEnabled(
      orgSettings.notifications.weeklyEarningsDigest?.enabled ?? false,
    );
    setWeeklyDigestDayOfWeek(
      orgSettings.notifications.weeklyEarningsDigest?.dayOfWeek ?? "Monday",
    );
    setWeeklyDigestSendTime(
      orgSettings.notifications.weeklyEarningsDigest?.sendTime ?? "08:00",
    );
  }, [orgSettings]);

  const settingsMutation = useMutation({
    mutationFn: async (update: {
      notifications?: Partial<{
        lowBalanceNotification: Partial<
          OrganizationNotificationSettingsDto["lowBalanceNotification"]
        >;
        pendingPayoutDigest: Partial<
          OrganizationNotificationSettingsDto["pendingPayoutDigest"]
        >;
        weeklyEarningsDigest: Partial<
          OrganizationNotificationSettingsDto["weeklyEarningsDigest"]
        >;
      }>;
    }) => {
      if (!orgSettings) return;
      const merged: OrganizationSettingsDto = {
        ...orgSettings,
        notifications: {
          ...orgSettings.notifications,
          lowBalanceNotification: {
            ...orgSettings.notifications.lowBalanceNotification,
            ...(update.notifications?.lowBalanceNotification ?? {}),
          },
          pendingPayoutDigest: {
            ...orgSettings.notifications.pendingPayoutDigest,
            ...(update.notifications?.pendingPayoutDigest ?? {}),
          },
          weeklyEarningsDigest: {
            ...orgSettings.notifications.weeklyEarningsDigest,
            ...(update.notifications?.weeklyEarningsDigest ?? {}),
          },
        },
      };
      const response = await axios.put<OrganizationSettingsDto>(
        "/api/organizations/current/settings",
        merged,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organizationSettings"] });
    },
    onError: (err: AxiosError<ApiErrorResponse>) => {
      toast.error(err.response?.data?.message || "Failed to save settings", {
        duration: Infinity,
      });
    },
  });

  const debouncedSaveThreshold = useDebouncedCallback((value: string) => {
    const normalized = parseFloat(value) || 0;
    settingsMutation.mutate({
      notifications: { lowBalanceNotification: { threshold: normalized } },
    });
  }, 500);

  const debouncedSavePendingDigestSendTime = useDebouncedCallback(
    (value: string) => {
      settingsMutation.mutate({
        notifications: { pendingPayoutDigest: { sendTime: value } },
      });
    },
    500,
  );

  const debouncedSaveWeeklyDigestSendTime = useDebouncedCallback(
    (value: string) => {
      settingsMutation.mutate({
        notifications: { weeklyEarningsDigest: { sendTime: value } },
      });
    },
    500,
  );

  const isSaving = settingsMutation.isPending;
  const inputClass =
    "pl-4 w-16 border border-gray-300 rounded px-2 py-0.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-brand-500";

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Notifications"
        parent={{ url: "/settings", label: "Settings" }}
      />
      <PageContentContainer>
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium">
                Low Balance Notifications
              </h2>
              <Toggle
                size="md"
                isSelected={isLowBalanceEnabled}
                onChange={(checked: boolean) => {
                  setIsLowBalanceEnabled(checked);
                  settingsMutation.mutate({
                    notifications: {
                      lowBalanceNotification: { enabled: checked },
                    },
                  });
                }}
                isDisabled={isSaving}
              />
            </div>
            <p className="text-sm text-gray-600 mt-3 flex items-center flex-wrap gap-1">
              <span>Notify admins when the funding balance drops below</span>
              <span className="relative inline-flex items-center">
                <span className="absolute left-2 text-gray-500 text-xs">$</span>
                <input
                  type="number"
                  value={lowBalanceThreshold}
                  onChange={(e) => {
                    setLowBalanceThreshold(e.target.value);
                    debouncedSaveThreshold(e.target.value);
                  }}
                  className={inputClass}
                  min="0"
                  step="1"
                />
              </span>
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium">Pending Payout Digest</h2>
              <Toggle
                size="md"
                isSelected={isDigestEnabled}
                onChange={(checked: boolean) => {
                  setIsDigestEnabled(checked);
                  settingsMutation.mutate({
                    notifications: {
                      pendingPayoutDigest: { enabled: checked },
                    },
                  });
                }}
                isDisabled={isSaving}
              />
            </div>
            <p className="text-sm text-gray-600 mt-3 flex items-center flex-wrap gap-1">
              <span>
                Send a daily email summary of pending payout requests at
              </span>
              <input
                type="time"
                value={digestSendTime}
                onChange={(e) => {
                  setDigestSendTime(e.target.value);
                  debouncedSavePendingDigestSendTime(e.target.value);
                }}
                className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium">Weekly Earnings Digest</h2>
              <Toggle
                size="md"
                isSelected={isWeeklyDigestEnabled}
                onChange={(checked: boolean) => {
                  setIsWeeklyDigestEnabled(checked);
                  settingsMutation.mutate({
                    notifications: {
                      weeklyEarningsDigest: { enabled: checked },
                    },
                  });
                }}
                isDisabled={isSaving}
              />
            </div>
            <p className="text-sm text-gray-600 mt-3 flex items-center flex-wrap gap-1">
              <span>
                Send a weekly push notification to talent members showing active
                opportunities every
              </span>
              <select
                className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={weeklyDigestDayOfWeek}
                onChange={(e) => {
                  const day = e.target.value as DayOfWeek;
                  setWeeklyDigestDayOfWeek(day);
                  settingsMutation.mutate({
                    notifications: { weeklyEarningsDigest: { dayOfWeek: day } },
                  });
                }}
                disabled={isSaving}
              >
                {daysOfWeek.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              <span>at</span>
              <input
                type="time"
                value={weeklyDigestSendTime}
                onChange={(e) => {
                  setWeeklyDigestSendTime(e.target.value);
                  debouncedSaveWeeklyDigestSendTime(e.target.value);
                }}
                className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </p>
          </div>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
